import { Link } from "react-router-dom";
import { Compass, ArrowRight } from "lucide-react";
import { trackCta } from "@/lib/analytics";
import FounderSection from "@/components/FounderSection";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { webPage, breadcrumb } from "@/lib/jsonLd";

export default function About() {
  const { t, lang } = useT();
  useSEO({
    title: "About StoicOs: An Operating System for the Inner Life | Virtue Guide",
    description: "StoicOs builds software for the examined life. We make Virtue Guide, an AI Stoic wisdom coach. Learn about our mission, founder, and approach to character development.",
    canonical: langCanonical("/about", lang),
    alternates: langAlternates("/about"),
    jsonLd: [
      webPage({ name: "About StoicOs", description: "StoicOs is a small studio building software for the inner life.", url: "/about", breadcrumbItems: [{ name: "Home", url: "/" }, { name: "About", url: "/about" }] }),
      breadcrumb([{ name: "Home", url: "/" }, { name: "About", url: "/about" }]),
    ],
  });
  const stats = [
    { label: t("about.statFounded"), value: "MMXXV" },
    { label: t("about.statBased"),   value: t("about.basedValue") },
    { label: t("about.statApp"),     value: "Virtue Guide" },
  ];

  return (
    <main data-testid="about-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-8">{t("about.eyebrow")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-bone tracking-tight leading-[1.0]">
          {t("about.headline1")}<br/><span className="italic text-gold">{t("about.headline2")}</span>
        </h1>

        <div className="gold-rule my-16" />

        <div className="space-y-8 text-lg text-bone/80 font-light leading-[1.85]">
          <p>
            <span className="font-serif text-2xl text-gold float-left mr-3 leading-none mt-2">S</span>
            {t("about.p1")}
          </p>

          <p>
            {t("about.p2_pre")}<em className="text-gold not-italic">{t("about.p2_em")}</em>{t("about.p2_post")}
          </p>

          <p>
            {t("about.p3")}
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {stats.map((s) => (
            <div key={s.label} className="bg-ink p-8">
              <p className="text-[10px] tracking-[0.25em] uppercase text-gold">{s.label}</p>
              <p className="mt-3 font-serif text-2xl text-bone">{s.value}</p>
            </div>
          ))}
        </div>

        {/* ---- Van de homepage verplaatste inhoud ---- */}
        <nav aria-label={t("about.nav.aria")} className="mt-24 flex flex-wrap gap-x-6 gap-y-3 text-[10px] tracking-[0.2em] uppercase text-bone/40">
          {[["#waarom", t("about.nav.why")], ["#founders-speech", t("about.nav.founders")], ["#digital-stoa", t("about.nav.stoa")], ["#disciplines", t("about.nav.disciplines")], ["#nooit-te-oud", t("about.nav.lifelong")]].map(([h, l]) => (
            <a key={h} href={h} className="hover:text-gold transition-colors">{l}</a>
          ))}
        </nav>

        <section id="waarom" className="mt-20 scroll-mt-28" data-testid="about-why-section">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{t("mission.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-bone leading-tight">
            {t("mission.headline1")} <span className="italic text-gold">{t("mission.headline2")}</span>
          </h2>
          <p className="mt-7 text-bone/70 font-light leading-[1.9] max-w-2xl">{t("mission.body")}</p>
        </section>

        <section id="founders-speech" className="mt-24 scroll-mt-28 -mx-6 md:-mx-10" data-testid="about-founders-speech">
          <FounderSection />
        </section>

        <section id="digital-stoa" className="mt-20 pt-16 border-t border-white/5 scroll-mt-28" data-testid="about-stoa-section">
          <h2 className="font-serif text-3xl md:text-4xl text-bone">{t("about.stoa.title")}</h2>
          <p className="mt-6 text-bone/70 font-light leading-[1.9] max-w-2xl">
            {t("about.stoa.body")}
          </p>
        </section>

        <section id="disciplines" className="mt-20 pt-16 border-t border-white/5 scroll-mt-28" data-testid="about-disciplines-section">
          <h2 className="font-serif text-3xl md:text-4xl text-bone">{t("about.disc.title")}</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {[[t("about.disc.logic.title"), t("about.disc.logic.body")],
              [t("about.disc.physics.title"), t("about.disc.physics.body")],
              [t("about.disc.ethics.title"), t("about.disc.ethics.body")]].map(([title, body]) => (
              <div key={title} className="border border-white/10 bg-[#0b0b0b] p-7">
                <h3 className="font-serif text-2xl text-gold">{title}</h3>
                <p className="mt-4 text-bone/65 font-light leading-relaxed text-sm">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="nooit-te-oud" className="mt-20 pt-16 border-t border-white/5 scroll-mt-28" data-testid="about-lifelong-section">
          <h2 className="font-serif text-3xl md:text-4xl text-bone">{t("about.lifelong.title")}</h2>
          <p className="mt-6 text-bone/70 font-light leading-[1.9] max-w-2xl">
            {t("about.lifelong.body")}
          </p>
          <blockquote className="mt-12 border-l-2 border-gold/60 pl-6 md:pl-8" data-testid="about-marcus-quote">
            <p className="font-serif text-2xl md:text-3xl italic text-bone leading-relaxed">
              “{t("about.lifelong.quote")}”
            </p>
            <cite className="mt-4 block text-[11px] tracking-[0.24em] uppercase text-gold not-italic">{t("about.lifelong.cite")}</cite>
          </blockquote>
          <div className="mt-10 text-bone/70 font-light leading-[2] max-w-2xl">
            <p>{t("about.lifelong.p1")}</p>
            <p className="mt-4">
              {t("about.lifelong.line1")}<br />
              {t("about.lifelong.line2")}<br />
              {t("about.lifelong.line3")}<br />
              {t("about.lifelong.line4")}
            </p>
            <p className="mt-6 font-serif text-xl italic text-gold/90">{t("about.lifelong.p3")}</p>
          </div>
        </section>

        <p className="mt-24 text-center text-[11px] tracking-[0.3em] uppercase text-bone/45" data-testid="about-closing">
          {t("about.closing")}
        </p>

        <div className="mt-20 flex items-center gap-3">
          <Compass size={20} color="#C5A059" strokeWidth={1.5} />
          <Link
            to="/support"
            data-testid="about-contact-link"
            onClick={() => trackCta("contact_from_about", { placement: "about_page" })}
            className="text-sm tracking-[0.2em] uppercase text-gold hover:text-gold-hover transition-colors inline-flex items-center gap-2"
          >
            {t("about.contactLink")} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </main>
  );
}
